import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Mail, Phone, ArrowLeft, Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async () => {
    if (!identifier.trim()) return toast.error("Informe " + (method === "email" ? "e-mail" : "telefone"));
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { identifier, method });
      toast.success(res.data.message || "Código enviado");
      // DEV convenience: prefill code if backend returned it (MVP)
      if (res.data.dev_code) {
        setCode(res.data.dev_code);
        toast.info(`Código (modo teste): ${res.data.dev_code}`, { duration: 8000 });
      }
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setLoading(false); }
  };

  const doReset = async () => {
    if (!code.trim() || newPassword.length < 6) return toast.error("Preencha o código e senha (mín. 6 caracteres)");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { identifier, code, new_password: newPassword });
      toast.success("Senha alterada! Faça login.");
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-0 -left-40 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

      <Card className="p-6 sm:p-8 max-w-md w-full relative z-10 shadow-xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <Lock className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Recuperar senha</h1>
            <p className="text-xs text-muted-foreground">
              {step === 1 ? "Enviaremos um código de verificação" : "Digite o código recebido"}
            </p>
          </div>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <Tabs value={method} onValueChange={setMethod}>
              <TabsList className="grid grid-cols-2 rounded-full w-full">
                <TabsTrigger value="email" data-testid="fp-method-email" className="rounded-full gap-2">
                  <Mail className="w-4 h-4" /> E-mail
                </TabsTrigger>
                <TabsTrigger value="phone" data-testid="fp-method-phone" className="rounded-full gap-2">
                  <Phone className="w-4 h-4" /> Telefone
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Input
              data-testid="fp-identifier"
              type={method === "email" ? "email" : "tel"}
              placeholder={method === "email" ? "seu@email.com" : "+55 11 99999-9999"}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <Button data-testid="fp-send-code" onClick={sendCode} disabled={loading} className="w-full rounded-full h-11">
              {loading ? "Enviando..." : "Enviar código"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Um código de 6 dígitos válido por 15 minutos
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <Input
              data-testid="fp-code"
              placeholder="Código de 6 dígitos"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              className="font-mono text-center tracking-widest text-lg"
            />
            <Input
              data-testid="fp-new-password"
              type="password"
              placeholder="Nova senha (mín. 6 caracteres)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Button data-testid="fp-reset-btn" onClick={doReset} disabled={loading} className="w-full rounded-full h-11">
              {loading ? "Alterando..." : "Alterar senha"}
            </Button>
            <button onClick={() => setStep(1)} className="w-full text-xs text-muted-foreground hover:text-primary">
              Voltar e reenviar código
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
